import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import useSiteConfig from "@/lib/useSiteConfig";
import ReviewCard from "@/components/common/ReviewCard";
import SocialValidation from "@/components/common/SocialValidation";
import FindYourStayCTA from "@/components/common/FindYourStayCTA";
import CountUp from "@/components/common/CountUp";
import { Star, Filter, X } from "lucide-react";

const PAGE_SIZE = 10;
const NUMBER_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
const spellOut = (n) => (n >= 0 && n < NUMBER_WORDS.length ? NUMBER_WORDS[n] : `${n}`);

export default function GuestLove() {
  const cfg = useSiteConfig();
  const propCount = Math.max(1, Number(cfg.active_properties_count || 0));
  const [reviews, setReviews] = useState([]);
  const [properties, setProperties] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [listing, setListing] = useState(searchParams.get("listing") || "all");
  const [source, setSource] = useState(searchParams.get("source") || "all");
  const [star, setStar] = useState(searchParams.get("star") || "all");
  const [page, setPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    api.get("/reviews").then(({ data }) => setReviews(data));
    api.get("/properties").then(({ data }) => setProperties(data));
  }, []);

  // If we arrived with a "#reviews" hash (from a Social Validation card),
  // smooth-scroll the user down to the review list instead of letting them
  // sit on a duplicate hero section.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#reviews") return;
    // Defer until the reviews section has rendered.
    const t = setTimeout(() => {
      const el = document.getElementById("reviews");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, []);

  // Persist filters into the URL so deep links from SocialValidation cards land correctly.
  useEffect(() => {
    const next = new URLSearchParams();
    if (listing !== "all") next.set("listing", listing);
    if (source !== "all") next.set("source", source);
    if (star !== "all") next.set("star", star);
    setSearchParams(next, { replace: true });
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing, source, star]);

  const sources = useMemo(() => {
    const set = new Set();
    reviews.forEach((r) => r.source_platform && set.add(r.source_platform));
    return ["all", ...Array.from(set).sort()];
  }, [reviews]);

  const filtered = useMemo(() => {
    return reviews.filter((r) => {
      if (listing !== "all" && r.property_slug !== listing) return false;
      if (source !== "all" && (r.source_platform || "").toLowerCase() !== source.toLowerCase()) return false;
      if (star !== "all") {
        const n = Number(r.rating || 0);
        if (star === "5" && n < 4.95) return false;
        if (star === "4" && n < 4) return false;
        if (star === "3" && n < 3) return false;
      }
      return true;
    });
  }, [reviews, listing, source, star]);

  const total = filtered.length;
  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < total;

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + Number(r.rating || 0), 0) / reviews.length)
    : 0;

  const resetFilters = () => { setListing("all"); setSource("all"); setStar("all"); };
  const anyFilterActive = listing !== "all" || source !== "all" || star !== "all";

  return (
    <div data-testid="guest-love-page">
      <Helmet>
        <title>Guest Love · Vyagram Experiences</title>
        <meta name="description" content="Real words from real Vyagram guests — across every property, every city." />
      </Helmet>
      <div className="mx-auto max-w-7xl px-4 pt-14 sm:px-6 lg:px-10">
        <p className="vyg-eyebrow">Guest Love</p>
        <h1 className="mt-3 font-display text-4xl sm:text-6xl">Words from our guests</h1>
        <p className="editorial-body mt-4 max-w-xl">Across thousands of stays, guests keep coming back for the quiet design, consistent service and small moments that last.</p>
      </div>

      <SocialValidation className="!py-12" />

      <div className="mx-auto max-w-7xl px-4 pb-14 sm:px-6 lg:px-10">
        <div className="flex flex-wrap items-center gap-6 rounded-2xl bg-mist/60 p-6">
          <div>
            <p className="font-display text-5xl text-forest"><CountUp value={avg} decimals={2} /></p>
            <p className="text-xs uppercase tracking-wider text-amber">Avg rating</p>
          </div>
          <div>
            <p className="font-display text-5xl text-forest"><CountUp value={reviews.length} suffix="+" /></p>
            <p className="text-xs uppercase tracking-wider text-amber">Reviews on site</p>
          </div>
          <div className="ml-auto hidden sm:block">
            <p className="text-xs uppercase tracking-wider text-amber">Now showing</p>
            <p className="font-display text-2xl text-forest">{visible.length} of {total}</p>
          </div>
        </div>

        {/* Filter bar — sticky on desktop, drawer toggle on mobile */}
        <div className="mt-8 flex items-center justify-between gap-3 sm:hidden">
          <button
            onClick={() => setMobileFiltersOpen((v) => !v)}
            data-testid="guest-love-filters-toggle"
            className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-white px-4 py-2 text-sm"
          >
            <Filter size={14}/> Filters{anyFilterActive ? " · active" : ""}
          </button>
          <p className="text-xs text-noir/60">{visible.length} of {total}</p>
        </div>

        <div className={`mt-4 grid gap-3 rounded-2xl border border-forest/10 bg-white p-4 sm:grid-cols-3 ${mobileFiltersOpen ? "" : "hidden"} sm:grid`} data-testid="guest-love-filters">
          <FilterSelect label="Listing" testId="filter-listing" value={listing} onChange={setListing}
            options={[{ value: "all", label: "All listings" }, ...properties.map((p) => ({ value: p.slug, label: p.display_name }))]} />
          <FilterSelect label="Source" testId="filter-source" value={source} onChange={setSource}
            options={sources.map((s) => ({ value: s, label: s === "all" ? "All sources" : s }))} />
          <FilterSelect label="Star rating" testId="filter-star" value={star} onChange={setStar}
            options={[
              { value: "all", label: "All ratings" },
              { value: "5", label: "5★ only" },
              { value: "4", label: "4★ and above" },
              { value: "3", label: "3★ and above" },
            ]} />
          {anyFilterActive && (
            <button onClick={resetFilters} data-testid="filter-reset"
              className="inline-flex items-center gap-1 self-end justify-self-end rounded-full border border-forest/20 px-3 py-1.5 text-xs text-forest sm:col-span-3">
              <X size={12}/> Clear filters
            </button>
          )}
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3" id="reviews" data-testid="guest-love-list">
          {visible.length === 0 && (
            <p className="col-span-full py-16 text-center text-sm text-noir/60">No reviews match these filters.</p>
          )}
          {visible.map((r) => {
            const prop = properties.find((p) => p.slug === r.property_slug);
            return <ReviewCard key={r.id} r={r} propertyName={prop?.display_name} propertySlug={prop?.slug} />;
          })}
        </div>

        <div className="mt-10 flex items-center justify-between">
          <p className="text-sm text-noir/70">
            Showing <span className="font-medium text-forest">{visible.length}</span> of{" "}
            <span className="font-medium text-forest">{total}</span>
            {anyFilterActive ? " filtered reviews" : " reviews"}
          </p>
          {hasMore && (
            <button
              onClick={() => setPage((p) => p + 1)}
              data-testid="guest-love-load-more"
              className="vyg-cta"
            >
              <Star size={14} className="fill-parchment text-parchment"/> Load 10 more
            </button>
          )}
        </div>
      </div>

      <FindYourStayCTA
        eyebrow="Want to experience it yourself?"
        title="Pick the stay that fits the story you want to tell"
        subtitle={`Every review here is from someone who picked one of our ${spellOut(propCount)} homes. Your turn.`}
      />
    </div>
  );
}

function FilterSelect({ label, testId, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-amber">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm">
        {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </label>
  );
}
